import { useEffect } from "react";
import { MotionGlobalConfig } from "framer-motion";

/**
 * Mount once on a page whose sections reveal on scroll via framer-motion
 * `whileInView` blocks (e.g. Home). On a very fast scroll (fling / scrollbar
 * drag / full-screen jump) it briefly switches framer-motion to instant
 * animations, so every block that comes into view during the fling appears
 * right away in its final state — no blank space and no placeholder.
 * Normal scrolling keeps the regular reveal animations.
 *
 * This replaces the old shimmer-skeleton overlay, which sat over the content
 * for up to ~2s and was positioned from the blocks' pre-animation (offset)
 * rects, so it often covered the wrong area.
 */

// How long reveals stay instant after the last fast scroll event. Long enough
// for the IntersectionObserver callbacks of the blocks the fling landed on
// (they arrive a frame or two after the scroll), short enough that slow
// scrolling right after a fling animates normally again.
const HOLD_MS = 350;

const FastScrollReveal = () => {
    useEffect(() => {
        let lastY = window.scrollY;
        let lastT = performance.now();
        let timer = null;

        const stopInstant = () => {
            timer = null;
            MotionGlobalConfig.instantAnimations = false;
        };

        const onScroll = () => {
            const y = window.scrollY;
            const now = performance.now();
            const dy = Math.abs(y - lastY);
            const dt = now - lastT;
            lastY = y;
            lastT = now;
            const vh = window.innerHeight || document.documentElement.clientHeight;
            const isFast = dy > vh || (dt > 0 && dt < 250 && dy / dt > 3.5 && dy > 60);
            if (!isFast) return;
            MotionGlobalConfig.instantAnimations = true;
            clearTimeout(timer);
            timer = setTimeout(stopInstant, HOLD_MS);
        };
        window.addEventListener("scroll", onScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", onScroll);
            clearTimeout(timer);
            MotionGlobalConfig.instantAnimations = false;
        };
    }, []);

    return null;
};

export default FastScrollReveal;
